import { RANGE_PRESETS, browserTimeZone, rangeFor, type RangePreset } from '../listeners/listenerStats';

/**
 * Full Data Report — types and the small pure pieces of the page.
 *
 * The report is built entirely by the API (`/admin/reports/full`): the panel
 * asks for it, polls the job and downloads the finished files. No rows are
 * ever loaded into the browser to build it.
 */

export type FullRangePreset = RangePreset | 'allTime';

/** The standard presets plus All time, which this report alone offers. */
export const FULL_RANGE_PRESETS: Array<{ value: FullRangePreset; label: string }> = [
  { value: 'allTime', label: 'All time' },
  ...RANGE_PRESETS.map((p) => (p.value === 'lastMonth' ? { ...p, label: 'Previous month' } : p)),
];

export interface FullReportSection {
  key: string;
  title: string;
  kind: 'master' | 'transactional' | 'config';
  description: string;
  filters: string[];
}

export interface FullReportCatalog {
  sections: FullReportSection[];
  filters: Record<string, string[]>;
  expiresAfterHours: number;
}

export type FullReportStatus = 'preparing' | 'processing' | 'ready' | 'failed';

export interface FullReportJob {
  id: string;
  status: FullReportStatus;
  requestedBy: { id: string; name: string };
  createdAt: string;
  finishedAt: string | null;
  expiresAt: string;
  range: { label: string; from: string | null; to: string | null; tz: string };
  sections: string[];
  filters: Record<string, string>;
  progress: { current: string | null; done: number; total: number };
  counts: Record<string, number>;
  totalRecords: number;
  files: { xlsxBytes: number | null; csv: Record<string, number> };
  warnings: string[];
  error: string | null;
}

export interface FullReportRequest {
  from?: string;
  to?: string;
  tz: string;
  label: string;
  sections: string[];
  filters?: Record<string, string>;
}

/**
 * The request for a preset. All time sends no dates; every other preset is the
 * same local-calendar range the rest of the panel uses. null = a custom range
 * with a date still missing.
 */
export function fullReportRequest(
  preset: FullRangePreset,
  now: Date,
  custom: { from: string; to: string },
  sections: string[],
  filters: Record<string, string | undefined>,
  tz: string = browserTimeZone(),
): FullReportRequest | null {
  const label = FULL_RANGE_PRESETS.find((p) => p.value === preset)?.label ?? 'Custom range';
  const kept = Object.fromEntries(
    Object.entries(filters)
      .map(([k, v]) => [k, (v ?? '').trim()] as const)
      .filter(([, v]) => v !== ''),
  );
  const base = { tz, sections, ...(Object.keys(kept).length ? { filters: kept } : {}) };
  if (preset === 'allTime') return { ...base, label };
  const range = rangeFor(preset, now, custom, tz);
  if (!range) return null;
  return {
    ...base,
    from: range.from,
    to: range.to,
    label: preset === 'custom' ? `${custom.from} to ${custom.to}` : label,
  };
}

export const isRunning = (job: FullReportJob | undefined): boolean =>
  job?.status === 'preparing' || job?.status === 'processing';

/** Which step of Preparing → Processing → Ready the job is on. */
export function jobStep(job: FullReportJob): { current: number; status: 'process' | 'finish' | 'error' } {
  if (job.status === 'failed') return { current: job.progress.done > 0 ? 1 : 0, status: 'error' };
  if (job.status === 'ready') return { current: 2, status: 'finish' };
  return { current: job.status === 'preparing' ? 0 : 1, status: 'process' };
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "Users: 12,450" lines for every section in the job, in sheet order. */
export function sectionRows(
  job: FullReportJob,
  catalog: FullReportCatalog | undefined,
): Array<{ key: string; title: string; records: number; csvBytes: number | null }> {
  const title = (key: string) => catalog?.sections.find((s) => s.key === key)?.title ?? key;
  return job.sections.map((key) => ({
    key,
    title: title(key),
    records: job.counts[key] ?? 0,
    csvBytes: job.files.csv[key] ?? null,
  }));
}

export const FILTER_LABELS: Record<string, string> = {
  userId: 'User ID',
  listenerId: 'Listener ID',
  listenerStatus: 'Listener status',
  callMode: 'Call type',
  callOutcome: 'Call status',
  ledgerReason: 'Transaction type',
  paymentStatus: 'Payment status',
  referralStatus: 'Referral status',
  verificationStatus: 'Verification status',
};
